/* ap-cart-totals.js — v3
 *
 * PRIMARY role: keep the cart DRAWER subtotal, savings, and checkout badge
 * up to date via /cart.js fetches after every cart mutation.
 *
 * SECONDARY role (fallback): on the CART PAGE, update the sticky checkout bar
 * and mobile total card ONLY for cases that section rendering can't cover:
 *   - bfcache restore (browser back button)
 *   - app/background cart changes (cart:updated without a section render)
 *
 * For regular quantity changes on the cart page, cart-items.js section
 * rendering is the source of truth (elements added to getSectionsToRender).
 */
(() => {
  if (window.__AP_CART_TOTALS_INIT__) return;
  window.__AP_CART_TOTALS_INIT__ = true;

  const $ = (s, r = document) => r.querySelector(s);

  const money = (c) =>
    (window.Shopify && Shopify.formatMoney)
      ? Shopify.formatMoney(c)
      : ('$' + ((c || 0) / 100).toFixed(2));

  function paint(now, save, itemCount) {
    // ── Drawer elements ──
    const nowEl = $('#ap-total-now');
    const saveEl = $('#ap-total-save');
    const savingsRow = $('#ap-savings-row');
    const dataEl = $('#ap-totals-data');

    if (nowEl) nowEl.textContent = money(now) + ' AUD';

    if (savingsRow) {
      if (save > 0) {
        savingsRow.style.display = '';
        if (saveEl) saveEl.textContent = '-' + money(save) + ' AUD';
      } else {
        savingsRow.style.display = 'none';
      }
    }

    if (typeof itemCount === 'number') {
      const badge = $('#ap-checkout-qty');
      if (badge) badge.textContent = String(itemCount);
    }

    if (dataEl) {
      dataEl.dataset.now = String(now || 0);
      dataEl.dataset.save = String(save || 0);
    }

    // ── Cart PAGE elements ──
    // Section rendering handles the initial update after quantity changes.
    // This paint also runs for bfcache restores, app/background mutations,
    // and any time the cart changes after the section render. We update ALL visible subtotal elements
    // so they never diverge from each other or from checkout.
    const stickyPrice = $('.cart-sticky-checkout__price');
    if (stickyPrice) stickyPrice.textContent = money(now);

    if (typeof itemCount === 'number') {
      const stickyQty = $('.cart-sticky-checkout__qty');
      if (stickyQty) stickyQty.textContent = '(' + itemCount + ' ' + (itemCount === 1 ? 'item' : 'items') + ')';
    }

    const cartTotalMobile = $('.th_cart_total_price_mobile');
    if (cartTotalMobile) cartTotalMobile.textContent = money(now);

    const cartTotalFloat = $('.th_cart_total_price_float');
    if (cartTotalFloat) cartTotalFloat.textContent = money(now);

    // Order details subtotal (uses money_with_currency in Liquid)
    const cartTotalSummary = $('.th_cart_total_price');
    if (cartTotalSummary) cartTotalSummary.textContent = money(now) + ' AUD';
  }

  // Cooldown: skip refresh if one completed less than 600ms ago
  let _lastRefreshAt = 0;
  let _refreshRunning = false;

  // Product data cache — compare_at_price doesn't change often
  const _productCache = {};

  async function refresh() {
    const hasDrawer = !!$('#ap-totals-data');
    const hasCartPage = !!$('.cart-sticky-checkout__price') || !!$('.th_cart_total_price');
    if (!hasDrawer && !hasCartPage) return;

    if (_refreshRunning) return;
    if (Date.now() - _lastRefreshAt < 600) return;

    _refreshRunning = true;
    try {
      const res = await fetch('/cart.js', {
        headers: { Accept: 'application/json', 'X-AP-Fresh': '1' },
        cache: 'no-store',
        credentials: 'same-origin'
      });
      if (!res.ok) return;

      const cart = await res.json();
      const now = cart.total_price || 0;

      let save = cart.total_discount || 0;

      // Compare-at savings per item (cart.js lacks compare_at_price)
      let compareAtSave = 0;
      const seen = {};
      const fetches = [];

      (cart.items || []).forEach((item) => {
        const handle = item.handle;
        if (!handle || seen[handle]) return;
        seen[handle] = true;

        if (_productCache[handle]) {
          const product = _productCache[handle];
          (cart.items || []).forEach((ci) => {
            if (ci.handle !== handle) return;
            const variant = (product.variants || []).find((v) => v.id === ci.variant_id);
            if (variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
              compareAtSave += (variant.compare_at_price - variant.price) * ci.quantity;
            }
          });
          return;
        }

        fetches.push(
          fetch('/products/' + handle + '.js')
            .then((r) => r.json())
            .then((product) => {
              _productCache[handle] = product;
              (cart.items || []).forEach((ci) => {
                if (ci.handle !== handle) return;
                const variant = (product.variants || []).find((v) => v.id === ci.variant_id);
                if (variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
                  compareAtSave += (variant.compare_at_price - variant.price) * ci.quantity;
                }
              });
            })
            .catch(() => {})
        );
      });

      if (fetches.length) await Promise.all(fetches);
      save = save + compareAtSave;

      paint(now, save, cart.item_count || 0);
    } catch (e) {
      // silent
    } finally {
      _lastRefreshAt = Date.now();
      _refreshRunning = false;
    }
  }

  let t = null;
  function queue(ms = 180) {
    clearTimeout(t);
    t = setTimeout(refresh, ms);
  }

  // ── Initialisation ──
  function initPaint() {
    const d = $('#ap-totals-data');
    if (d) {
      paint(+d.dataset.now || 0, +d.dataset.save || 0);
    }
    queue(120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPaint);
  } else {
    initPaint();
  }

  // bfcache restore — DOMContentLoaded doesn't re-fire
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) queue(50);
  });

  // ── Event listeners ──

  // Theme/app events (background mutations, drawer refresh, etc.)
  [
    'cart:updated',
    'cart:refresh',
    'cart:change',
    'ajaxProduct:added',
    'shipping:estimated',
    'shopify:section:load',
    'dispatch:cart-drawer:refresh'
  ].forEach((ev) => {
    document.addEventListener(ev, () => queue(220));
  });

  // Quantity interactions in drawer
  document.addEventListener('click', (e) => {
    if (e.target.closest(
      '#cart-drawer [data-action="increase"], #cart-drawer [data-action="decrease"], ' +
      '#cart-drawer .qty__button, #cart-drawer .quantity__button, ' +
      '#cart-drawer button[name="plus"], #cart-drawer button[name="minus"]'
    )) queue(350);
  }, true);

  document.addEventListener('change', (e) => {
    if (e.target.closest(
      '#cart-drawer [data-quantity-input], #cart-drawer input[name^="updates"], #cart-drawer input[type="number"]'
    )) queue(300);
  }, true);

  document.addEventListener('input', (e) => {
    if (e.target.closest(
      '#cart-drawer [data-quantity-input], #cart-drawer input[name^="updates"], #cart-drawer input[type="number"]'
    )) queue(450);
  }, true);

  // Line-item changes — from applySections (drawer) or cart-items.js (cart page).
  // On the cart page, section rendering already updated the DOM with fresh HTML.
  // We DON'T re-fetch here to avoid racing with the section render.
  // The drawer doesn't include cart data in the event detail, so it falls
  // through to the fetch path.
  document.addEventListener('on:line-item:change', (e) => {
    if (e.detail && e.detail.cart && typeof e.detail.cart.total_price === 'number') {
      // Cart data available — paint directly (matches section render source).
      // Do NOT set _lastRefreshAt here — another script may mutate the cart right after
      // the section render, and the subsequent cart:updated needs to trigger a
      // real /cart.js fetch to pick up those changes.
      const cart = e.detail.cart;
      paint(cart.total_price || 0, cart.total_discount || 0, cart.item_count);
    } else {
      queue(300);
    }
  });
})();
